import {
  PicklistSelectionDecisionRule,
  PicklistSelectionMatchType,
} from './picklistSelectionDecision.shared';

/**
 * Encodes rule precedence for combining stable/advisor outcomes:
 * consensus/conflict first, then exact-over-none, then guarded fuzzy, else insufficient confidence.
 */
export const resolvePicklistSelectionDecisionRule = ({
  stableMatchType,
  stablePickIndex,
  stableConfidence,
  stableMargin,
  advisorMatchType,
  advisorPickIndex,
  advisorConfidence,
  advisorMargin,
  exactConfidenceThreshold,
  fuzzyConfidenceThreshold,
  fuzzyMarginThreshold,
}: {
  stableMatchType: PicklistSelectionMatchType;
  stablePickIndex: number | null;
  stableConfidence: number;
  stableMargin: number;
  advisorMatchType: PicklistSelectionMatchType;
  advisorPickIndex: number | null;
  advisorConfidence: number;
  advisorMargin: number;
  exactConfidenceThreshold: number;
  fuzzyConfidenceThreshold: number;
  fuzzyMarginThreshold: number;
}): PicklistSelectionDecisionRule => {
  // If both engines resolve, agreement is consensus; disagreement is conflict.
  if (stablePickIndex != null && advisorPickIndex != null) {
    return stablePickIndex === advisorPickIndex
      ? PicklistSelectionDecisionRule.Consensus
      : PicklistSelectionDecisionRule.Conflict;
  }

  const stableIsExact = stableMatchType === PicklistSelectionMatchType.Exact;
  const advisorIsExact = advisorMatchType === PicklistSelectionMatchType.Exact;
  const stableIsFuzzy = stableMatchType === PicklistSelectionMatchType.Fuzzy;
  const advisorIsFuzzy = advisorMatchType === PicklistSelectionMatchType.Fuzzy;
  const stableIsNone = stableMatchType === PicklistSelectionMatchType.None;
  const advisorIsNone = advisorMatchType === PicklistSelectionMatchType.None;

  const hasExactOverNone =
    (stableIsExact && advisorIsNone && stableConfidence >= exactConfidenceThreshold) ||
    (advisorIsExact && stableIsNone && advisorConfidence >= exactConfidenceThreshold);

  if (hasExactOverNone) {
    return PicklistSelectionDecisionRule.ExactOverNone;
  }

  const hasGuardedFuzzy =
    (stableIsFuzzy &&
      advisorIsNone &&
      stableConfidence >= fuzzyConfidenceThreshold &&
      stableMargin >= fuzzyMarginThreshold) ||
    (advisorIsFuzzy &&
      stableIsNone &&
      advisorConfidence >= fuzzyConfidenceThreshold &&
      advisorMargin >= fuzzyMarginThreshold);

  if (hasGuardedFuzzy) {
    return PicklistSelectionDecisionRule.GuardedFuzzy;
  }

  return PicklistSelectionDecisionRule.InsufficientConfidence;
};
